import React, { useState, useCallback, SyntheticEvent } from 'react';
import classNames from 'classnames';
import { Stack, ControlLink, Overlay, Text } from '@moda/om';
import { useLocation } from 'react-router-dom';
import { formatPrice } from '../../lib/formatPrice';
import { reportError } from '../../lib/reportError';
import { useCart, CartVersion } from '../../hooks/useCart';
import { useUser } from '../../hooks/useUser';
import { LoadingBalls } from '../LoadingBalls';
import { useErrorModal } from '../../hooks/useErrorModal';
import { hrefFor } from '../../routers';
import { useReward } from './useReward';

import './RewardSummary.scss';

enum Mode {
  Resting,
  Saving,
  Applied
}

interface Props {
  theme?: 'box' | 'simple';
}

export const RewardSummary: React.FC<Props> = ({ theme = 'box' }) => {
  const { cart } = useCart();
  const { user } = useUser();
  const { showErrorModal } = useErrorModal();
  const location = useLocation();

  const rewardSummary = cart.attributes.rewardSummary;

  const [mode, setMode] = useState(user.userLoyaltyStatus ? Mode.Applied : Mode.Resting);
  const { applyReward } = useReward();

  if (cart.attributes.version === CartVersion.MiniBag) return null;

  const submitCode = useCallback(
    async (reward: boolean) => {
      setMode(Mode.Saving);

      try {
        const result = await applyReward(reward);
        if (result?.data?.updateCart.data[0].attributes.rewardSummary?.useLoyaltyRewards) {
          setMode(Mode.Applied);
        } else {
          setMode(Mode.Resting);
        }
      } catch (error) {
        setMode(Mode.Resting);
        showErrorModal();
        reportError(error);
      }
    },
    [setMode, applyReward, showErrorModal]
  );

  const onApply = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      submitCode(true);
    },
    [submitCode]
  );

  const onRemove = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      submitCode(false);
    },
    [submitCode]
  );

  if (!rewardSummary?.redeemableAmount.price) return null;

  return (
    <>
      <div className={classNames('RewardSummary', `RewardSummary--${theme}`)}>
        <Stack space={2}>
          <div className="RewardSummary__head">
            <div className="RewardSummary__title">M'O Rewards</div>
            {!rewardSummary?.useLoyaltyRewards && (
              <ControlLink
                className="RewardSummary__apply"
                aria-label="Add Rewards"
                onClick={onApply}
              >
                Apply
              </ControlLink>
            )}
            {rewardSummary?.useLoyaltyRewards && (
              <ControlLink
                className="RewardSummary__remove"
                aria-label="Remove Rewards"
                onClick={onRemove}
              >
                Remove
              </ControlLink>
            )}
          </div>
          {location.pathname === hrefFor.ShoppingBagPage() && rewardSummary.useLoyaltyRewards ? (
            <div className="RewardSummary__content-right">
              {`-${formatPrice(
                cart.attributes.paymentSummary.rewardsAmount.currency,
                cart.attributes.paymentSummary.rewardsAmount.price
              )}`}
            </div>
          ) : (
            <div className="RewardSummary__content">
              {`${formatPrice(
                rewardSummary.redeemableAmount.currency,
                rewardSummary.redeemableAmount.price
              )} in rewards available.`}
            </div>
          )}
        </Stack>
      </div>

      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />
        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
